// Radar Chart Component (ECharts wrapper)
import { useEffect, useRef, useState } from 'preact/hooks'
import { getGlobalStats } from '../../modules/globalStats.js'
import { radarHoverMetric, basis, domain } from '../../state/globalSignals.js'
import * as echarts from 'echarts'

export default function RadarChart({ cellData }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [globalStats, setGlobalStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Load global stats on mount with priority
    setIsLoading(true)
    getGlobalStats()
      .then(stats => {
        setGlobalStats(stats)
        setIsLoading(false)
      })
      .catch(err => {
        console.error('Failed to load global stats:', err)
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!chartRef.current || !globalStats || !cellData) return

    // Initialize or get existing chart instance
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current)
    }

    // ALWAYS use gravimetric basis - no longer dependent on signals
    const currentBasis = 'grav'
    const currentDomain = 'energy-power'  // Always energy-power domain
    
    let metric1Value, metric2Value, metric1Stats, metric2Stats
    let labels, metric1Label, metric2Label
    
    if (currentDomain === 'capacity-current') {
      // Capacity-Current Domain
      // Capacity: C_C10 in C (absolute), C/kg (grav), C/m³ (vol)
      // Current: I_peak in A (absolute), A/kg (grav), A/m³ (vol)
      metric1Value = currentBasis === 'abs' ? (cellData?.electrical?.C_C10 || 0)
                   : currentBasis === 'vol' ? (cellData?.electrical?.C_C10_vol || 0)
                   : (cellData?.electrical?.C_C10_grav || 0)
      
      metric2Value = currentBasis === 'abs' ? (cellData?.electrical?.I_peak || 0)
                   : currentBasis === 'vol' ? (cellData?.electrical?.I_peak_vol || 0)
                   : (cellData?.electrical?.I_peak_grav || 0)
      
      metric1Stats = globalStats.capacity[currentBasis]
      metric2Stats = globalStats.current[currentBasis]
      
      labels = ['Cell-to-Cell Variation', 'Capacity', 'Post-Formation Loss', 'Current']
      metric1Label = 'Capacity'
      metric2Label = 'Current'
    } else {
      // Energy-Power Domain (default)
      // Energy: W_C10 in J (absolute), J/kg (grav), J/m³ (vol)
      // Power: P_peak in W (absolute), W/kg (grav), W/m³ (vol)
      metric1Value = currentBasis === 'abs' ? (cellData?.electrical?.W_C10 || 0)
                   : currentBasis === 'vol' ? (cellData?.electrical?.W_C10_vol || 0)
                   : (cellData?.electrical?.W_C10_grav || 0)
      
      metric2Value = currentBasis === 'abs' ? (cellData?.electrical?.P_peak || 0)
                   : currentBasis === 'vol' ? (cellData?.electrical?.P_peak_vol || 0)
                   : (cellData?.electrical?.P_peak_grav || 0)
      
      metric1Stats = globalStats.energy[currentBasis]
      metric2Stats = globalStats.power[currentBasis]
      
      labels = ['Cell-to-Cell Variation', 'Energy', 'Post-Formation Loss', 'Power']
      metric1Label = 'Energy'
      metric2Label = 'Power'
    }
    
    const batchVar = cellData?.batch?.CAP_spread_std_rel || 0  // relative (0.05 = 5%)
    // Post-Formation Loss: can be negative (capacity gain) or positive (capacity loss)
    // Negative = cell improved, Positive = cell lost capacity
    const initialAging = cellData?.aging?.CAP_loss_rel ?? 0  // relative, keep sign (negative = better)
    
    // Normalize to 0-100% based on global min/max for current basis and domain
    // For metric1 and metric2: higher is better
    // For batch variation and aging: lower is better (invert)
    const metric1Score = metric1Stats.max > metric1Stats.min
      ? ((metric1Value - metric1Stats.min) / (metric1Stats.max - metric1Stats.min)) * 100
      : 0
    
    const metric2Score = metric2Stats.max > metric2Stats.min
      ? ((metric2Value - metric2Stats.min) / (metric2Stats.max - metric2Stats.min)) * 100
      : 0
    
    // Inverted: lower variation = higher score
    const consistencyScore = globalStats.maxBatchVar > globalStats.minBatchVar
      ? ((globalStats.maxBatchVar - batchVar) / (globalStats.maxBatchVar - globalStats.minBatchVar)) * 100
      : 100
    
    // Inverted: lower aging = higher score
    // Note: initialAging can be negative (capacity gain = excellent) or positive (capacity loss)
    // Scale: min (could be negative, best) to max (positive, worst)
    // Score: 100% at min (best), 0% at max (worst)
    const durabilityScore = globalStats.maxInitialAging > globalStats.minInitialAging
      ? ((globalStats.maxInitialAging - initialAging) / (globalStats.maxInitialAging - globalStats.minInitialAging)) * 100
      : 100
    
    const values = [
      Math.max(0, Math.min(100, consistencyScore)),
      Math.max(0, Math.min(100, metric1Score)),
      Math.max(0, Math.min(100, durabilityScore)),
      Math.max(0, Math.min(100, metric2Score)),
    ]
    
    // Calculate average scores based on current basis and domain
    let avgMetric1Value, avgMetric2Value
    
    if (currentDomain === 'capacity-current') {
      avgMetric1Value = globalStats.avgCapacityBasis?.[currentBasis] || globalStats.avgCapacity
      avgMetric2Value = globalStats.avgCurrentBasis?.[currentBasis] || globalStats.avgCurrent
    } else {
      avgMetric1Value = globalStats.avgEnergyBasis?.[currentBasis] || globalStats.avgEnergy
      avgMetric2Value = globalStats.avgPowerBasis?.[currentBasis] || globalStats.avgPower
    }
    
    const avgMetric1Score = metric1Stats.max > metric1Stats.min
      ? ((avgMetric1Value - metric1Stats.min) / (metric1Stats.max - metric1Stats.min)) * 100
      : 50
    
    const avgMetric2Score = metric2Stats.max > metric2Stats.min
      ? ((avgMetric2Value - metric2Stats.min) / (metric2Stats.max - metric2Stats.min)) * 100
      : 50
    
    const avgConsistencyScore = globalStats.maxBatchVar > globalStats.minBatchVar
      ? ((globalStats.maxBatchVar - globalStats.avgBatchVar) / (globalStats.maxBatchVar - globalStats.minBatchVar)) * 100
      : 50
    
    const avgDurabilityScore = globalStats.maxInitialAging > globalStats.minInitialAging
      ? ((globalStats.maxInitialAging - globalStats.avgInitialAging) / (globalStats.maxInitialAging - globalStats.minInitialAging)) * 100
      : 50
    
    const meanValues = [
      Math.max(0, Math.min(100, avgConsistencyScore)),
      Math.max(0, Math.min(100, avgMetric1Score)),
      Math.max(0, Math.min(100, avgDurabilityScore)),
      Math.max(0, Math.min(100, avgMetric2Score))
    ]
    
    // Helper to format values for display based on basis and domain
    const formatMetric1Value = (val) => {
      if (currentDomain === 'capacity-current') {
        // Capacity: C in Coulombs -> Ah
        if (currentBasis === 'abs') return `${(val/3600).toFixed(2)} Ah`
        if (currentBasis === 'vol') return `${(val/3600000).toFixed(1)} Ah/L`
        return `${(val/3600).toFixed(1)} Ah/kg`
      } else {
        // Energy: J -> Wh
        if (currentBasis === 'abs') return `${(val/3600).toFixed(1)} Wh`
        if (currentBasis === 'vol') return `${(val/3600000).toFixed(1)} Wh/L`
        return `${(val/3600).toFixed(1)} Wh/kg`
      }
    }
    
    const formatMetric2Value = (val) => {
      if (currentDomain === 'capacity-current') {
        // Current: A
        if (currentBasis === 'abs') return `${val.toFixed(1)} A`
        if (currentBasis === 'vol') return `${(val/1000000).toFixed(1)} kA/L`
        return `${val.toFixed(0)} A/kg`
      } else {
        // Power: W
        if (currentBasis === 'abs') return `${val.toFixed(0)} W`
        if (currentBasis === 'vol') return `${(val/1000000).toFixed(1)} kW/L`
        // Gravimetric: switch to kW/kg if >= 1000 W/kg
        if (currentBasis === 'grav' && val >= 1000) {
          return `${(val/1000).toFixed(2)} kW/kg`
        }
        return `${val.toFixed(0)} W/kg`
      }
    }
    
    console.log('📊 Radar Chart Values (ECharts):', {
      cell: cellData?.name || 'unknown',
      domain: currentDomain,
      basis: currentBasis,
      raw: {
        metric1: formatMetric1Value(metric1Value),
        metric2: formatMetric2Value(metric2Value),
        batchVar: `${(batchVar*100).toFixed(2)}%`,
        initialAging: `${(initialAging*100).toFixed(2)}%`
      },
      scores: {
        metric1: `${metric1Score.toFixed(1)}%`,
        metric2: `${metric2Score.toFixed(1)}%`,
        consistency: `${consistencyScore.toFixed(1)}%`,
        durability: `${durabilityScore.toFixed(1)}%`
      }
    })

    const option = {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
      tooltip: {
        show: false
      },
      radar: {
        indicator: labels.map((name, idx) => ({
          name,
          max: 100
        })),
        shape: 'polygon',
        radius: '65%',
        splitNumber: 5,
        center: ['50%', '52%'],
        axisName: {
          formatter: (value, indicator) => {
            // Get index from labels array
            const idx = labels.indexOf(value)
            
            // Get the actual value for this axis
            // New order: [Batch Variation, Energy/Capacity, Initial Aging, Power/Current]
            let actualValue = ''
            if (idx === 0) {
              // Batch Variation (top)
              actualValue = `${(batchVar * 100).toFixed(2)}%`
            } else if (idx === 1) {
              // Energy/Capacity (left)
              actualValue = formatMetric1Value(metric1Value)
            } else if (idx === 2) {
              // Initial Aging (bottom)
              // Negative = capacity gain, Positive = capacity loss
              const agingPercent = initialAging * 100
              if (agingPercent < 0) {
                actualValue = `${agingPercent.toFixed(2)}%`  // Show as negative (gain)
              } else if (agingPercent > 0) {
                actualValue = `${agingPercent.toFixed(2)}%`  // Show without plus (loss)
              } else {
                actualValue = `0.00%`  // Exactly 0
              }
            } else if (idx === 3) {
              // Power/Current (right)
              actualValue = formatMetric2Value(metric2Value)
            }
            
            // Return formatted text with value below label (no hover state)
            return `{label|${value}}\n{value|${actualValue}}`
          },
          rich: {
            label: {
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 14,
              fontWeight: '400',
              fontFamily: 'Big John, sans-serif'
            },
            value: {
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 12,
              fontWeight: '600',
              fontFamily: 'Roboto, sans-serif',
              lineHeight: 16
            }
          }
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(234, 91, 12, 0.15)',
            width: 1.5
          }
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: [
              'rgba(234, 91, 12, 0.03)',
              'rgba(234, 91, 12, 0.06)',
              'rgba(234, 91, 12, 0.03)',
              'rgba(234, 91, 12, 0.06)',
              'rgba(234, 91, 12, 0.03)'
            ]
          }
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(234, 91, 12, 0.3)',
            width: 2
          }
        }
      },
      series: [
        {
          name: 'Global Average',
          type: 'radar',
          data: [
            {
              value: meanValues,
              name: 'Global Average',
              lineStyle: {
                color: '#888',
                width: 2,
                type: [5, 5]
              },
              areaStyle: {
                color: 'rgba(136, 136, 136, 0.08)'
              },
              symbol: 'circle',
              symbolSize: 6,
              itemStyle: {
                color: '#888',
                borderColor: '#fff',
                borderWidth: 1
              }
            }
          ]
        },
        {
          name: 'This Cell',
          type: 'radar',
          data: [
            {
              value: values,
              name: cellData?.name || 'This Cell',
              lineStyle: {
                color: '#ea5b0c',
                width: 3,
                shadowColor: 'rgba(234, 91, 12, 0.5)',
                shadowBlur: 10
              },
              areaStyle: {
                color: {
                  type: 'radial',
                  x: 0.5,
                  y: 0.5,
                  r: 0.5,
                  colorStops: [
                    { offset: 0, color: 'rgba(234, 91, 12, 0.4)' },
                    { offset: 1, color: 'rgba(234, 91, 12, 0.1)' }
                  ]
                }
              },
              symbol: 'circle',
              symbolSize: 8,
              itemStyle: {
                color: '#ea5b0c',
                borderColor: '#fff',
                borderWidth: 2,
                shadowColor: 'rgba(234, 91, 12, 0.8)',
                shadowBlur: 8
              }
            }
          ]
        }
      ]
    }

    chartInstance.current.setOption(option)

    // Cleanup on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
    }
  }, [cellData, globalStats, radarHoverMetric.value])  // Removed basis.value and domain.value dependencies

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize()
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (isLoading || !globalStats) {
    return (
      <div ref={chartRef} className="radar-chart" style="height:100%;width:100%;min-height:260px;display:flex;align-items:center;justify-content:center;">
        <span style="color:#999;font-size:12px;">Loading chart...</span>
      </div>
    )
  }

  return <div ref={chartRef} className="radar-chart" style="height:100%;width:100%;min-height:260px;"></div>
}
